import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Toaster } from '@/components/ui/sonner';
import { CityProvider } from '@/context/CityContext';
import { PassportProvider } from '@/context/PassportContext';
import { AuthProvider } from '@/context/AuthContext';
import Shell from '@/components/Shell';
import PageTransition from '@/components/PageTransition';
import GuidedTour from '@/components/GuidedTour';
import AuthCallback from '@/components/AuthCallback';
import Landing from '@/pages/Landing';
import Home from '@/pages/Home';
import NeedCategory from '@/pages/NeedCategory';
import MapScreen from '@/pages/MapScreen';
import Translate from '@/pages/Translate';
import Arrival from '@/pages/Arrival';
import Legal from '@/pages/Legal';
import Explore from '@/pages/Explore';
import Profile from '@/pages/Profile';
import TripDiary from '@/pages/TripDiary';
import ShareView from '@/pages/ShareView';
import TrivlyAI from '@/pages/TrivlyAI';
import { NavVisibilityProvider } from '@/context/NavVisibilityContext';
import { tap } from '@/lib/haptics';
import './App.css';

/* Global tap haptic — any button/anchor/[role=button] triggers a short vibration.
 * Cheap (one listener) and lets us skip per-element wiring. */
function GlobalTapHaptics() {
  useEffect(() => {
    const handler = (e) => {
      const el = e.target?.closest?.('button, a[href], [role="button"]');
      if (!el) return;
      if (el.hasAttribute('disabled') || el.getAttribute('aria-disabled') === 'true') return;
      tap();
    };
    document.addEventListener('pointerdown', handler, { passive: true });
    return () => document.removeEventListener('pointerdown', handler);
  }, []);
  return null;
}

function AnimatedRoutes() {
  const location = useLocation();
  // CRITICAL: detect Emergent Auth fragment SYNCHRONOUSLY (during render, before any /auth/me race).
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/onboarding" element={<PageTransition><Landing /></PageTransition>} />
        <Route path="/" element={<PageTransition><Home /></PageTransition>} />
        <Route path="/ai" element={<PageTransition><TrivlyAI /></PageTransition>} />
        <Route path="/trivly" element={<PageTransition><TrivlyAI /></PageTransition>} />
        <Route path="/explore" element={<PageTransition><Explore /></PageTransition>} />
        <Route path="/profile" element={<PageTransition><Profile /></PageTransition>} />
        <Route path="/diary" element={<PageTransition><TripDiary /></PageTransition>} />
        <Route path="/share/:sid" element={<PageTransition><ShareView /></PageTransition>} />
        <Route path="/need/:category" element={<PageTransition><NeedCategory /></PageTransition>} />
        <Route path="/map" element={<PageTransition><MapScreen /></PageTransition>} />
        <Route path="/translate" element={<PageTransition><Translate /></PageTransition>} />
        <Route path="/arrival" element={<PageTransition><Arrival /></PageTransition>} />
        <Route path="/legal/:kind" element={<PageTransition><Legal /></PageTransition>} />
      </Routes>
    </AnimatePresence>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <CityProvider>
        <PassportProvider>
          <NavVisibilityProvider>
            <BrowserRouter>
              <GlobalTapHaptics />
              <Shell>
                <AnimatedRoutes />
              </Shell>
              <GuidedTour />
              <Toaster position="top-center" richColors closeButton theme="light" />
            </BrowserRouter>
          </NavVisibilityProvider>
        </PassportProvider>
      </CityProvider>
    </AuthProvider>
  );
}
